import { defineMessages, useIntl } from 'react-intl';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import { getPhotoFromId, getPhotoUrl } from '@alltrails/shared/utils/requests/photoRequests';
import PhotoSize from '@alltrails/types/PhotoSize';
import type TrailDetails from '@alltrails/core/types/TrailDetails';
import type TrailPhoto from '@alltrails/core/types/TrailPhoto';
import { useStaticMapUrl } from '@alltrails/networking/hooks/useStaticMapUrl';
import { getTrailLocationName } from '@alltrails/trails';
import type { TrailPhotoLocationInfo } from '@alltrails/trails';

const messages = defineMessages({
  HERO_ALT: {
    defaultMessage: '{list} | AllTrails.com'
  },
  STATIC_MAP_ALT: {
    defaultMessage: '{name} map'
  },
  PHOTOS_BUTTON: {
    defaultMessage: '{photoCount, plural, one {# photo} other {# photos}}'
  }
});

export default function useTrailDefaultImage({
  defaultPhoto,
  mapId,
  photoPreviewId,
  photoSize,
  trailId,
  trailPhotoLocationInfo,
  useSatelliteStaticMap,
  trailDetails
}: {
  defaultPhoto?: TrailPhoto;
  mapId: number;
  photoPreviewId?: string;
  photoSize: PhotoSize;
  trailId?: number;
  trailPhotoLocationInfo: TrailPhotoLocationInfo;
  useSatelliteStaticMap?: boolean;
  trailDetails?: TrailDetails;
  seoTrailPhotoAltTextTest?: boolean;
}) {
  const intl = useIntl();
  const { hasPermission } = useAuthorization();
  const publicBaseUrl = usePublicBaseUrl();
  const getStaticMapUrl = useStaticMapUrl();
  const { name: trailName, area, city, region, country } = trailPhotoLocationInfo;

  let photoUrl = '';

  const getTrailPhotoTitle = (trailName: string, trailDetails: TrailDetails, trailLocation: string) => {
    const hasCityLocation = trailDetails.location?.city;
    const hasRegionLocation = trailDetails.location?.regionName || trailDetails?.location?.region;

    if (trailDetails.area?.name) {
      return intl.formatMessage({ defaultMessage: 'Photo of {trailName} in {parkName}' }, { trailName, parkName: trailDetails?.area?.name });
    } else if (hasCityLocation && hasRegionLocation) {
      return intl.formatMessage({ defaultMessage: 'Photo of {trailName} near {cityAndRegionName}' }, { trailName, cityAndRegionName: trailLocation });
    } else if (hasCityLocation) {
      return intl.formatMessage({ defaultMessage: 'Photo of {trailName} near {cityName}' }, { trailName, cityName: trailLocation });
    } else if (hasRegionLocation) {
      return intl.formatMessage(
        { defaultMessage: 'Photo of {trailName} in {regionName}' },
        { trailName, regionName: trailDetails.location?.regionName }
      );
    } else {
      return intl.formatMessage({ defaultMessage: 'Photo of {trailName}' }, { trailName });
    }
  };

  if (photoPreviewId && hasPermission({ permission: 'trails:manage' })) {
    photoUrl = getPhotoFromId(photoPreviewId, PhotoSize.DesktopBanner, publicBaseUrl);
  } else if (defaultPhoto) {
    photoUrl = getPhotoUrl({ id: defaultPhoto.id, photoHash: defaultPhoto.photoHash }, photoSize, true /* isOptimized */);
  } else if (useSatelliteStaticMap && trailId) {
    photoUrl = getStaticMapUrl({ id: trailId, size: '1280x480', scale: 2, style: 'satellite', type: 'trail' });
  } else {
    photoUrl = getStaticMapUrl({ id: mapId, size: '1280x582', type: 'map' });
  }

  const formatHeroAlt = () => {
    if (trailDetails) {
      const trailLocation = getTrailLocationName(trailDetails);
      return getTrailPhotoTitle(trailName, trailDetails, trailLocation);
    } else {
      return intl.formatMessage(messages.HERO_ALT, {
        list: [trailName, area, city, region, country].filter(Boolean).join(', ')
      });
    }
  };

  const alt = defaultPhoto ? formatHeroAlt() : intl.formatMessage(messages.STATIC_MAP_ALT, { name: trailName });

  return { defaultPhotoAlt: alt, defaultPhotoUrl: photoUrl };
}
